import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { getStates, resolveStateToCode, type State } from '@/lib/api/statesService'
import { showSuccess, showApiError, showError } from '@/lib/utils/notifications'
import { sendAnalytics } from '@/lib/utils/analytics'
import { cleanPhoneNumber, formatPhoneNumber } from '@/features/invitee/utils/inviteeFormatters'
import {
  organizationFormSchema,
  type OrganizationFormInput,
  type OrganizationFormValues,
} from '../types/schemas'
import {
  getOrganization,
  patchOrganizationCertificateDraft,
  saveOrganization,
  type OrganizationLogoFilePick,
} from '../services/organizationsService'
import type { OrganizationSubmitPayload } from '../types/organization'
import { ORGANIZATION_FORM_DEFAULT_VALUES } from '../constants/editPage'
import { coerceCharity, fileLabelFromServerPath, normalizeHexColorForInput } from '../utils/editPage'
import type { OrganizationEditAssets } from './useOrganizationEditAssets'

type FormParams = {
  orgId: number | undefined
  isEdit: boolean
  navigate: (path: string) => void
  assets: OrganizationEditAssets
}

function trimOptional(v: string | number | null | undefined): string | undefined {
  if (v === null || v === undefined) return undefined
  const t = String(v).trim()
  return t ? t : undefined
}

export function useOrganizationEditForm({ orgId, isEdit, navigate, assets }: FormParams) {
  const {
    setLogoServerSrc,
    setLogoServerLine,
    setLogoFiles,
    setDefaultFormServerLine,
    setDefaultFormFiles,
    logoFiles,
    setPrimaryLogoRequiredError,
    uploadFilePayload,
  } = assets

  const [loading, setLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [draftSaving, setDraftSaving] = useState(false)
  const [certificateTemplateDraft, setCertificateTemplateDraft] = useState<string | null>(null)
  const [stateOptions, setStateOptions] = useState<State[]>([])
  const loadedRef = useRef(false)
  const publishedCertificateRef = useRef<string>('')

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    getFieldState,
    formState: { errors, isValid, dirtyFields },
  } = useForm<OrganizationFormInput>({
    resolver: zodResolver(organizationFormSchema),
    mode: 'onChange',
    defaultValues: ORGANIZATION_FORM_DEFAULT_VALUES,
  })

  const isCertificateDirty = Boolean(dirtyFields.certificate_template)

  const statesSorted = useMemo(
    () => [...stateOptions].sort((a, b) => a.state_name.localeCompare(b.state_name, 'en')),
    [stateOptions],
  )

  useEffect(() => {
    if (isEdit) return
    void getStates()
      .then(setStateOptions)
      .catch(() => setStateOptions([]))
  }, [isEdit])

  const load = useCallback(
    async (id: number) => {
      setLoading(true)
      try {
        const [org, st] = await Promise.all([getOrganization(id), getStates()])
        setStateOptions(st)
        const orgState = resolveStateToCode(org.org_state, st)
        if (id === 1) {
          publishedCertificateRef.current = org.certificate_template ?? ''
          setCertificateTemplateDraft(org.certificate_template_draft ?? null)
        }
        reset({
          org_name: org.org_name ?? '',
          org_abbrev: org.org_abbrev ?? '',
          charity: coerceCharity(org.charity),
          org_website: org.org_website ?? '',
          org_phone: org.org_phone
            ? formatPhoneNumber(cleanPhoneNumber(String(org.org_phone)))
            : '',
          org_email: org.org_email ?? '',
          org_address: org.org_address ?? '',
          org_city: org.org_city ?? '',
          org_state: orgState,
          org_zipcode: org.org_zipcode ?? '',
          org_color: normalizeHexColorForInput(org.org_color),
          email_footer: org.email_footer ?? '',
          system_announcement: org.system_announcement ?? '',
          about_content: org.about_content ?? '',
          about_content_applicant: org.about_content_applicant ?? '',
          about_content_pmc: org.about_content_pmc ?? '',
          stock_letter: org.stock_letter ?? '',
          lost_revenue_content: org.lost_revenue_content ?? '',
          payment_information_row: org.payment_information_row ?? '',
          skip_claim_form: org.skip_claim_form ?? '',
          default_payment_statement_form: org.default_payment_statement_form ?? '',
          certificate_template: org.certificate_template_draft
            ? org.certificate_template_draft
            : org.certificate_template ?? '',
        })
        setLogoServerSrc({
          org_logo: (org.org_logo ?? '').trim(),
          org_swoosh_logo: (org.org_swoosh_logo ?? '').trim(),
          org_image: (org.org_image ?? '').trim(),
        })
        setLogoServerLine({
          org_logo: fileLabelFromServerPath(org.org_logo),
          org_swoosh_logo: fileLabelFromServerPath(org.org_swoosh_logo),
          org_image: fileLabelFromServerPath(org.org_image),
        })
        setLogoFiles({})
        setDefaultFormServerLine({
          ach_form_file: fileLabelFromServerPath(org.ach_form_file),
          statement_instruction_file: fileLabelFromServerPath(org.statement_instruction_file),
        })
        setDefaultFormFiles({})
      } catch (error) {
        showApiError(error)
      } finally {
        setLoading(false)
      }
    },
    [reset, setLogoServerSrc, setLogoServerLine, setLogoFiles, setDefaultFormServerLine, setDefaultFormFiles],
  )

  useEffect(() => {
    if (!isEdit || !orgId) return
    if (loadedRef.current) return
    loadedRef.current = true
    void load(orgId)
  }, [isEdit, orgId, load])

  const updateCertificateDraft = useCallback(
    async (isDiscard = false) => {
      if (orgId !== 1 || draftSaving || submitting) return
      setDraftSaving(true)
      try {
        const certVal = (getValues('certificate_template') ?? '').trim()
        const draftPayload = {
          certificate_template_draft: isDiscard ? '' : certVal,
        }
        await patchOrganizationCertificateDraft(1, draftPayload)
        if (isDiscard) {
          showSuccess('Contract Template Draft Discard Successfully')
          setCertificateTemplateDraft(null)
          setValue('certificate_template', publishedCertificateRef.current, { shouldDirty: false })
        } else {
          showSuccess('Certificate Template Draft Save Successfully')
          setCertificateTemplateDraft(certVal)
          setValue('certificate_template', certVal, { shouldDirty: false })
        }
      } catch (error) {
        showApiError(error)
      } finally {
        setDraftSaving(false)
      }
    },
    [orgId, draftSaving, submitting, getValues, setValue],
  )

  // 3-minute draft auto-save for org_id === 1 (Angular startTimer / updateDraft)
  useEffect(() => {
    if (orgId !== 1 || !isEdit) return
    const interval = setInterval(() => {
      const fieldState = getFieldState('certificate_template')
      if (fieldState.isDirty) {
        void updateCertificateDraft(false)
      }
    }, 180000)
    return () => clearInterval(interval)
  }, [orgId, isEdit, getFieldState, updateCertificateDraft])

  const buildPayloadFromValues = useCallback(
    (values: OrganizationFormInput | OrganizationFormValues): OrganizationSubmitPayload => {
    return {
      org_name: values.org_name.trim(),
      org_abbrev: values.org_abbrev.trim(),
      charity: values.charity,
      org_website: trimOptional(values.org_website),
      org_phone: values.org_phone ? cleanPhoneNumber(values.org_phone) : undefined,
      org_email: trimOptional(values.org_email),
      org_address: trimOptional(values.org_address),
      org_city: trimOptional(values.org_city),
      org_state: trimOptional(values.org_state),
      org_zipcode: trimOptional(values.org_zipcode),
      org_color: trimOptional(values.org_color),
      email_footer: values.email_footer.trim(),
      system_announcement: values.system_announcement?.trim() ?? '',
      about_content: trimOptional(values.about_content),
      about_content_applicant: trimOptional(values.about_content_applicant),
      about_content_pmc: trimOptional(values.about_content_pmc),
      stock_letter: trimOptional(values.stock_letter),
      lost_revenue_content: trimOptional(values.lost_revenue_content),
      payment_information_row: trimOptional(values.payment_information_row),
      skip_claim_form: trimOptional(values.skip_claim_form),
      default_payment_statement_form: trimOptional(values.default_payment_statement_form),
      certificate_template: trimOptional(values.certificate_template),
    }
  }, [])

  const uploadSingleFile = useCallback(
    async (filePayload: OrganizationLogoFilePick) => {
      if (!isEdit || !orgId) return
      setSubmitting(true)
      try {
        const values = getValues()
        const payload = buildPayloadFromValues(values)
        await saveOrganization(payload, orgId, filePayload)
        showSuccess('Data Updated Successfully')
        await load(orgId)
      } catch (error) {
        showApiError(error)
      } finally {
        setSubmitting(false)
      }
    },
    [isEdit, orgId, getValues, buildPayloadFromValues, load],
  )

  const onSubmit = async (values: OrganizationFormValues) => {
    if (!isEdit && !logoFiles.org_logo) {
      setPrimaryLogoRequiredError(true)
      showError('Please Enter Organization Logo')
      return
    }
    setPrimaryLogoRequiredError(false)
    setSubmitting(true)
    try {
      const payload = buildPayloadFromValues(values)
      await saveOrganization(payload, orgId, uploadFilePayload)
      sendAnalytics('click', isEdit ? 'edit organization' : 'add organization', isEdit ? String(orgId ?? '') : '')
      showSuccess('Data Updated Successfully')
      navigate('/organizations')
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  return {
    loading,
    submitting,
    draftSaving,
    certificateTemplateDraft,
    isCertificateDirty,
    updateCertificateDraft,
    uploadSingleFile,
    stateOptions,
    statesSorted,
    register,
    control,
    handleSubmit,
    errors,
    isValid,
    onSubmit,
  }
}

export type OrganizationEditFormState = ReturnType<typeof useOrganizationEditForm>
